'use client';

import { FC, use, useEffect, useMemo, useState } from 'react';

import { User, UserGroups } from '@ravago/shared/authentication';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { DynamicPageUrl, RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';
import { DropdownOption } from '@ravago/shared/radiance/components/input-field/components/Dropdown/Dropdown';
import { MultiselectOption } from '@ravago/shared/radiance/components/input-field/components/Multiselect/Multiselect';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePathname, useSearchParams } from 'next/navigation';
import { firstBy } from 'thenby';

import { useDialogContext } from '../../../../hooks/use-dialog-context';
import { useDocumentShare } from '../../../../hooks/use-document-share';
import { getLanguageCode, getLanguageNameFromLanguageCode } from '../../../../utils/locale/locale.utils';
import SanitizeDataUtils from '../../../../utils/sanitize-data/sanitize-data.utils';
import NotFoundIllustration from '../../../illustrations/not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { Link } from '../../../shared/link/link';
import { DynamicPageLink } from '../../dynamic-page-link/dynamic-page-link';
import { useRelatedDocuments } from '../hooks/use-related-documents';

import { RelatedDocumentCategory } from './related-document-category/related-document-category';
import { RelatedDocumentHeader } from './related-document-header/related-document-header';

interface EmptyStateVars {
   noResult: string[];
   goTo: string;
   noSearchResult: string;
}

interface Props {
   dynamicPage: { type?: string; id?: string; extraId?: string; detailPageName?: string };
   contentTypeOverviewBaseRoute: DynamicPageUrl | null;
   hasPublicTdsDownloadPermission: boolean;
   hasPublicShowTdsUomPermission: boolean;
   toRequestQuotation: { href?: string; target: string; title: string; disabled: boolean };
   channelUOMs: NormalizedUnitOfMeasure[];
   ulProspectorId: number | null;
   documentAnchor?: string;
   canDownloadDocumentsAnonymously?: boolean;
   globals: RendererGlobals;
   hasShowTdsUomPermission: boolean;
   hasSharePermission?: boolean;
   relatedDocumentsPromise: Promise<{ key: string; documents: RelatedDocument[] }[]>;
   user?: User;
}

export const RelatedDocumentList: FC<Props> = ({
   hasPublicTdsDownloadPermission,
   dynamicPage,
   contentTypeOverviewBaseRoute,
   toRequestQuotation,
   hasPublicShowTdsUomPermission,
   channelUOMs,
   globals,
   ulProspectorId,
   canDownloadDocumentsAnonymously,
   documentAnchor,
   hasShowTdsUomPermission,
   hasSharePermission,
   relatedDocumentsPromise,
   user
}) => {
   const translate = useTranslations();
   const { phone, tabPort, desktop } = useBreakpoint('desktop');
   const { locale } = globals;
   const relatedDocuments = use(relatedDocumentsPromise);
   const language = getLanguageCode(locale);
   const [search, setSearch] = useState<string>('');
   const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
   const [category, setCategory] = useState<string | undefined>(undefined);
   const [filterLanguages, setFilterLanguages] = useState<string[] | undefined>();
   const [accordionsOpen, setAccordionsOpen] = useState<number[]>([]);
   const [filteredOptions, setFilteredOptions] = useState<{ key: string; documents: RelatedDocument[] }[]>(relatedDocuments);
   const [sharedDocuments, setSharedDocuments] = useState<Array<RelatedDocument> | undefined>();
   const [openTDS, setOpenTDS] = useState<boolean>(false);
   const [selectedUlTdsLanguage, setSelectedUlTdsLanguage] = useState<DownloadOption>();
   const [selectedUlTdsUOM, setSelectedUlTdsUOM] = useState<DownloadOption>();

   const { shareDocumentList, setShareList, downloadDocumentList, setDownloadList } = useDocumentShare();
   const queryParams = useSearchParams();
   const pathname = usePathname();
   const { state } = useDialogContext();

   const { canDownloadDocument, handleDownload, handleViewDocument, handleAllDownload } = useRelatedDocuments({
      globals,
      dynamicPage,
      relatedDocuments,
      canDownloadDocumentsAnonymously,
      hasPublicTdsDownloadPermission,
      selectedUlTdsLanguage,
      selectedUlTdsUOM
   });

   const getEmptyState: EmptyStateVars = useMemo(() => {
      switch (dynamicPage.type) {
         case 'products':
            return {
               noSearchResult: 'product.documents.no-search-results-found',
               goTo: 'related-documents.go-to-products',
               noResult: ['product.documents.no-results-found-1', 'product.documents.no-results-found-2']
            };
         case 'technologies':
            return {
               noSearchResult: 'product.documents.no-search-results-found-technology',
               goTo: 'related-documents.go-to-technologies',
               noResult: ['product.documents.no-results-found-technology-1', 'product.documents.no-results-found-technology-2']
            };
         case 'brands':
            return {
               noSearchResult: 'product.documents.no-search-results-found-brand',
               goTo: 'related-documents.go-to-brands',
               noResult: ['product.documents.no-results-found-brand-1', 'product.documents.no-results-found-brand-2']
            };
         case 'markets':
            return {
               noSearchResult: 'product.documents.no-search-results-found-market',
               goTo: 'related-documents.go-to-markets',
               noResult: ['product.documents.no-results-found-market-1', 'product.documents.no-results-found-market-2']
            };
         default:
            return { noResult: [''], goTo: '', noSearchResult: '' };
      }
   }, [dynamicPage.type]);

   const getAvailableLanguages = useMemo(() => {
      const normalized = relatedDocuments
         .map((value) => value.documents)
         .flat()
         .filter((d) => d.language !== '')
         .map((d) => d.language.toLowerCase())
         .reduce((entities, value) => ({ ...entities, [value]: value }), {});
      return Object.keys(normalized);
   }, [relatedDocuments]);

   const getAvailableLanguagesOptions = useMemo(
      () =>
         getAvailableLanguages
            .map(
               (key): MultiselectOption => ({
                  id: key,
                  checked: filterLanguages?.includes(key) ?? false,
                  label: getLanguageNameFromLanguageCode(key, locale)
               })
            )
            .sort(firstBy((l) => l, { ignoreCase: true })),
      [filterLanguages, getAvailableLanguages]
   );

   const hasExtraInitialLanguage = useMemo(
      () => language.toLowerCase() !== 'en' && getAvailableLanguagesOptions.map((l) => l.id).includes(language.toLowerCase()),
      [getAvailableLanguagesOptions, language]
   );

   const isFilterApplied = useMemo(
      () => !!category || !filterLanguages || (filterLanguages && filterLanguages.length !== (hasExtraInitialLanguage ? 2 : 1)),
      [category, filterLanguages, hasExtraInitialLanguage]
   );

   const isFilterOrSearchApplied = useMemo(() => search !== '' || isFilterApplied, [search, isFilterApplied]);

   const handleUlTdsLanguageChange = (lan: DownloadOption | undefined) => {
      if (lan) {
         setSelectedUlTdsLanguage(lan);
      }
   };
   const handleUlTdsUOMChange = (uom: DownloadOption | undefined) => {
      if (uom) {
         setSelectedUlTdsUOM(uom);
      }
   };

   const getCategories = () => filteredOptions.map((value) => ({ id: value.key, value: value.key }));

   const getAmountOfDocumentCategories = () => filteredOptions.length;

   const handleSearchChange = (searchChange: string) => {
      setSearch(searchChange);
   };
   const handleSearchClear = () => {
      setSearch('');
   };

   const handleCategoryChange = (categoryChange: DropdownOption | undefined) => {
      setCategory(categoryChange?.value);
   };

   const handleCategoryClear = () => {
      setCategory(undefined);
   };

   const getNumberOfFilters = () => ((category ? 1 : 0) + (filterLanguages?.length ?? 0)).toString();

   const handleLanguageChange = (languageChange: MultiselectOption[]) => {
      const formattedLanguages = languageChange.filter((languageOption) => languageOption.checked).map((value) => value.id);
      setFilterLanguages(formattedLanguages.length > 0 ? formattedLanguages : undefined);
   };

   const handleLanguageClear = () => {
      setFilterLanguages(hasExtraInitialLanguage ? ['en', language.toLowerCase()] : ['en']);
   };

   const handleIsFilterOpenToggle = (open: boolean) => {
      setIsFilterOpen(open);
   };

   const handleClearAll = () => {
      handleLanguageClear();
      handleCategoryClear();
      handleSearchClear();
   };

   const handleAccordionsOpen = (index: number | number[]) => {
      if (Array.isArray(index)) {
         setAccordionsOpen(index);
         return;
      }
      if (accordionsOpen.includes(index)) {
         setAccordionsOpen(accordionsOpen.filter((value) => value !== index));
         return;
      }
      setAccordionsOpen([...accordionsOpen, index]);
   };

   const handleShareChecked = (document: RelatedDocument) => {
      const index = shareDocumentList.documents.findIndex((doc) => doc.id === document.id && doc.type === document.type);
      if (index >= 0) {
         const newshareDocumentList = { ...shareDocumentList };
         newshareDocumentList.documents = shareDocumentList.documents.filter((_, i) => i !== index);
         setShareList(newshareDocumentList);
      } else {
         setShareList({
            documents: [...shareDocumentList.documents, document],
            detailPageName: dynamicPage.detailPageName ?? ''
         });
      }
   };

   const getSharedDocumentsFromLocalStorage = () => {
      const shared = localStorage.getItem('sharedDocuments');
      if (!shared) {
         setSharedDocuments(undefined);
         return;
      }

      const parsedShared = JSON.parse(shared);
      if (!relatedDocuments || !SanitizeDataUtils.isArrayOfString(parsedShared)) return;
      if (parsedShared[0] === `${dynamicPage.type}-${dynamicPage.id}`) {
         setSharedDocuments(
            relatedDocuments
               .map((relatedDocument) => relatedDocument.documents)
               .flat()
               .filter((doc) => parsedShared.includes(doc.id) && !doc.isInternal)
         );
      }
   };

   useEffect(() => {
      setFilterLanguages(hasExtraInitialLanguage ? ['en', language.toLowerCase()] : ['en']);
   }, [hasExtraInitialLanguage]);

   useEffect(() => {
      let filtered = relatedDocuments;
      filtered = filtered.map((value) => ({
         key: value.key,
         documents: value.documents.filter((document) => filterLanguages?.includes(document.language.toLowerCase()) ?? true)
      }));
      if (category) filtered = filtered.filter((value) => value.key === category);
      if (search) {
         const searchValue = search.toLowerCase();
         filtered = filtered.map((value) => ({
            key: value.key,
            documents: value.documents.filter(
               (document) =>
                  value.key.toLowerCase().includes(searchValue) ||
                  document.type.toLowerCase().includes(searchValue) ||
                  document.description.toLowerCase().includes(searchValue) ||
                  document.name.toLowerCase().includes(searchValue)
            )
         }));
      }

      setFilteredOptions(filtered.filter((value) => value.documents.length > 0));
   }, [search, relatedDocuments, category, filterLanguages]);

   useEffect(() => {
      if (downloadDocumentList.length > 0) {
         handleAllDownload(downloadDocumentList).then(() => setDownloadList([]));
      }
   }, [downloadDocumentList]);

   useEffect(() => {
      const defaultUom = channelUOMs.find((uom) => uom.default) ?? channelUOMs[0];
      const translatedUom = defaultUom ? { id: defaultUom.id, value: translate(defaultUom.value) } : undefined;
      if (!selectedUlTdsUOM) setSelectedUlTdsUOM(translatedUom);
   }, [channelUOMs]);

   useEffect(() => {
      if (state.internal?.canBeDownloaded && state.documentId) {
         const document = relatedDocuments
            .map((byCat) => byCat.documents)
            .flat()
            .find((doc) => doc.id === state.documentId);
         if (document) handleDownload(document, undefined, undefined, false);
      }
   }, [state.documentId, state.internal?.canBeDownloaded]);

   useEffect(() => {
      if (isFilterOrSearchApplied) handleAccordionsOpen([...Array(relatedDocuments.length).keys()]);
      else handleAccordionsOpen([]);
   }, [isFilterOrSearchApplied]);

   useEffect(() => {
      const documentIds = queryParams.get('documentIds');
      if (documentIds) {
         localStorage.setItem('sharedDocuments', JSON.stringify([`${dynamicPage.type}-${dynamicPage.id}`, ...documentIds.split(';')]));

         if (documentAnchor) {
            document.getElementById(documentAnchor)?.scrollIntoView({ behavior: 'smooth' });
         }

         const currentParams = new URLSearchParams(queryParams.toString());
         currentParams.delete('documentIds');

         window.history.pushState(null, '', `${pathname}${currentParams.toString() ? `?${currentParams.toString()}` : ''}`);
      }
   }, []);

   useEffect(() => {
      getSharedDocumentsFromLocalStorage();
   }, [relatedDocuments, queryParams]);

   useEffect(() => {
      if (!openTDS) return; // Nothing to do
      if (!relatedDocuments) return; // Documents are still loading, handle this when the relatedDocuments are loaded

      if (filteredOptions?.some((option) => option.key === 'Datasheets') && !accordionsOpen.includes(0)) {
         setAccordionsOpen((prev) => [...prev, 0]);
      }
      setOpenTDS(false);
   }, [filteredOptions, openTDS]);

   useEffect(() => {
      const handleCheckTdsClick = () => setOpenTDS(true);

      window.addEventListener('onCheckTds', handleCheckTdsClick);
      return () => {
         window.removeEventListener('onCheckTds', handleCheckTdsClick);
      };
   }, []);

   return (
      <Box direction="column" gap="lg">
         <RelatedDocumentHeader
            disableAllFilters={!isFilterOrSearchApplied && getAmountOfDocumentCategories() === 0}
            filteredOptions={filteredOptions}
            availableLanguages={getAvailableLanguagesOptions}
            isFilterOpen={isFilterOpen}
            isFilterApplied={isFilterApplied}
            iconOnlyFilterButton={phone || tabPort}
            search={search}
            category={category}
            categories={getCategories()}
            numberOfFilters={getNumberOfFilters()}
            onCategoryChange={(cat) => handleCategoryChange(cat)}
            onCategoryClear={() => handleCategoryClear()}
            onClearAll={() => handleClearAll()}
            onLanguageChange={(lang) => handleLanguageChange(lang)}
            onLanguageClear={() => handleLanguageClear()}
            onSearch={(searchChange) => handleSearchChange(searchChange)}
            onSetFilterOpen={(open) => handleIsFilterOpenToggle(open)}
            onSearchClear={() => handleSearchClear()}
         />
         {!relatedDocuments && (
            <Box direction="column" items="center">
               <Spinner />
            </Box>
         )}
         {sharedDocuments && (
            <RelatedDocumentCategory
               user={user}
               onDismiss={() => getSharedDocumentsFromLocalStorage()}
               channelUOMs={channelUOMs.map((uom) => ({ ...uom, value: translate(uom.value) as any }))}
               onDocumentDownload={(document, selectedLanguageId, selectedUOMId) => handleDownload(document, selectedLanguageId, selectedUOMId)}
               onViewDocument={(document, selectedLanguageId, selectedUOMId) => handleViewDocument(document, selectedLanguageId, selectedUOMId)}
               onShareChecked={(document) => handleShareChecked(document)}
               onLanguageChange={(lan) => handleUlTdsLanguageChange(lan)}
               onUOMChange={(uom) => handleUlTdsUOMChange(uom)}
               selectedLanguage={selectedUlTdsLanguage}
               selectedUOM={selectedUlTdsUOM}
               hasSharePermission={hasSharePermission}
               publicShowTdsUom={hasPublicShowTdsUomPermission}
               showTdsUom={hasShowTdsUomPermission}
               locale={locale}
               detailPageName={dynamicPage.detailPageName}
               documents={sharedDocuments}
               sharedWithYou
               ulProspectorId={ulProspectorId?.toString()}
               canDownloadDocumentsAnonymously={canDownloadDocumentsAnonymously}
               canDownloadDocument={canDownloadDocument}
               superCondensedView={phone}
               condensedView={!desktop}
            />
         )}
         {getAmountOfDocumentCategories() > 0 && relatedDocuments && (
            <Box direction="column">
               {filteredOptions.map((value, index) => (
                  <RelatedDocumentCategory
                     user={user}
                     channelUOMs={channelUOMs.map((uom) => ({ ...uom, value: translate(uom.value) as any }))}
                     onDocumentDownload={(document, selectedLanguageId, selectedUOMId) => handleDownload(document, selectedLanguageId, selectedUOMId)}
                     onViewDocument={(document, selectedLanguageId, selectedUOMId) => handleViewDocument(document, selectedLanguageId, selectedUOMId)}
                     onShareChecked={(document) => handleShareChecked(document)}
                     onLanguageChange={(lan) => handleUlTdsLanguageChange(lan)}
                     onUOMChange={(uom) => handleUlTdsUOMChange(uom)}
                     selectedLanguage={selectedUlTdsLanguage}
                     selectedUOM={selectedUlTdsUOM}
                     hasSharePermission={hasSharePermission}
                     ulProspectorId={ulProspectorId?.toString()}
                     publicShowTdsUom={hasPublicShowTdsUomPermission}
                     showTdsUom={hasShowTdsUomPermission}
                     locale={locale}
                     key={value.key}
                     detailPageName={dynamicPage.detailPageName}
                     documents={value.documents}
                     accordionTitle={value.key}
                     onToggle={() => handleAccordionsOpen(index)}
                     accordionOpen={accordionsOpen.includes(index)}
                     canDownloadDocumentsAnonymously={canDownloadDocumentsAnonymously}
                     canDownloadDocument={canDownloadDocument}
                     superCondensedView={phone}
                     condensedView={!desktop}
                  />
               ))}
            </Box>
         )}
         {getAmountOfDocumentCategories() === 0 && relatedDocuments && (
            <Box direction="column" items="center">
               <EmptyState
                  label={isFilterOrSearchApplied ? translate(getEmptyState.noSearchResult) : getEmptyState.noResult.map((label) => translate(label))}
                  image={<NotFoundIllustration />}
                  title={translate('related-documents.empty-title')}
               />
               <Box direction={phone ? 'column' : 'row'} justify="center">
                  {isFilterOrSearchApplied && !phone ? (
                     <Button onClick={() => handleClearAll()} variant="secondary">
                        {translate('related-documents.reset-search')}
                     </Button>
                  ) : (
                     <DynamicPageLink
                        id={phone ? 'dynamic-link__document-list__product-overview__phone' : 'dynamic-link__document-list__product-overview'}
                        locale={locale}
                        underline={phone ? undefined : 'none'}
                        baseRoute={contentTypeOverviewBaseRoute ?? null}
                     >
                        <Button variant="secondary">{translate(getEmptyState.goTo)}</Button>
                     </DynamicPageLink>
                  )}
                  {user &&
                     !user.groups?.includes(UserGroups.digitalPlatformGuest) &&
                     !user.groups?.includes(UserGroups.digitalPlatformPendingVerification) &&
                     dynamicPage.type === 'products' && (
                        <Link
                           disabled={toRequestQuotation.disabled}
                           underline="none"
                           href={toRequestQuotation.href ?? ''}
                           target={toRequestQuotation.target}
                           title={toRequestQuotation.title}
                        >
                           <Button disabled={toRequestQuotation.disabled} variant="primary">
                              {translate('related-documents.request')}
                           </Button>
                        </Link>
                     )}
               </Box>
            </Box>
         )}
      </Box>
   );
};
